import {
  AuditOutlined,
  BarChartOutlined,
  BookOutlined,
  DatabaseOutlined,
  FileTextOutlined,
  HomeOutlined,
  IdcardOutlined,
  GiftOutlined,
  HeartOutlined,
  ImportOutlined,
  LogoutOutlined,
  SettingOutlined,
  TeamOutlined,
  SwapOutlined,
  UserOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';
import type { NavGroupId } from '../auth/policy';

/**
 * A symbol per destination, so the sidebar can be scanned rather than read.
 *
 * **Keyed by path, and kept out of `policy.ts`.** That module decides who may
 * reach what and is deliberately free of React — a `ReactNode` in the
 * authorization table would make the rules importable only from a component,
 * and `policy.test.ts` sweeps it in plain Node. Same reasoning as
 * `SECTION_BLURBS` on the home screen: the navigation is one list, and what it
 * *looks like* hangs off the path rather than living inside the rule.
 *
 * A path with no entry would render no icon rather than a placeholder — a new
 * destination should not quietly inherit a generic symbol — and
 * `nav-icons.test.ts` fails until somebody chooses one. The wedding-anniversary
 * report is why that is a test rather than a convention: it shipped with the gap.
 *
 * ### Every one is `aria-hidden`, and that is not tidying
 *
 * Ant Design renders an icon as `<span role="img" aria-label="user">`, which
 * joins the accessible name of whatever contains it — so a menu entry stopped
 * being called "Jemaat" and started being called "user Jemaat". A screen reader
 * would announce the decoration before the destination, and `auth.spec.ts`
 * failed on the exact-name lookup, which is how this was caught. The label
 * beside the symbol already says where the link goes; the symbol is there to be
 * seen, not read out.
 *
 * ### On the choices
 *
 * The two attestation desks are the only pair that has to be told apart at a
 * glance, and they are opposites — somebody arriving and somebody leaving — so
 * they get arrows pointing in opposite directions rather than two variations on
 * a person.
 *
 * The reports are rosters and registers, so they get a present, a heart, a
 * two-way arrow and a book rather than charts: a birthday arriving, a marriage
 * remembered, people moving in both directions, and the register of those the
 * church has buried. The two rosters sit side by side and are the pair most
 * easily confused, so the anniversary is the heart rather than a second
 * variation on a gift. A book because that is what a register of deaths is,
 * and nothing louder — nothing about that entry should look like an alert. The
 * chart moved up to the section's own row, where it still means "the numbers".
 *
 * `/jemaat` and `/pengguna` are both about people and started with the same
 * symbol, which defeats the point: a sidebar where two entries look identical
 * is one a person still has to read. Members get the person, staff accounts get
 * the identity card — the thing that distinguishes them is having a login, not
 * being a different kind of human.
 */
export const NAV_ICONS: Record<string, ReactNode> = {
  '/': <HomeOutlined aria-hidden />,
  '/jemaat': <UserOutlined aria-hidden />,
  '/keluarga': <TeamOutlined aria-hidden />,
  '/atestasi-masuk': <ImportOutlined aria-hidden />,
  '/atestasi-keluar': <LogoutOutlined aria-hidden />,
  '/permintaan-perubahan': <FileTextOutlined aria-hidden />,
  '/wilayah': <EnvironmentOutlined aria-hidden />,
  '/pengguna': <IdcardOutlined aria-hidden />,
  '/laporan/ulang-tahun': <GiftOutlined aria-hidden />,
  '/laporan/ulang-tahun-pernikahan': <HeartOutlined aria-hidden />,
  '/laporan/atestasi': <SwapOutlined aria-hidden />,
  '/laporan/kematian': <BookOutlined aria-hidden />,
  '/pengaturan': <SettingOutlined aria-hidden />,
  '/catatan-audit': <AuditOutlined aria-hidden />,
};

/**
 * The symbol on a collapsible section's own row.
 *
 * Keyed by group id rather than folded into `NAV_ICONS`, which is keyed by
 * path: a section is not a destination, and one map holding both would be a map
 * whose keys mean two different things — the first step towards a group id
 * being passed somewhere a route is expected.
 *
 * `DatabaseOutlined` rather than a folder: the section is not a container
 * somebody filed things into, it is the congregation's record, and the six
 * screens inside it are the ways of reading and changing it.
 */
export const NAV_GROUP_ICONS: Record<NavGroupId, ReactNode> = {
  dataJemaat: <DatabaseOutlined aria-hidden />,
  // The chart the single Laporan entry carried before it became a section. The
  // section is still "the numbers", and moving the symbol up to its row keeps
  // the sidebar recognisable to anyone who used the flat version.
  laporan: <BarChartOutlined aria-hidden />,
};
